import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CloudDownload,
  CloudUpload,
  Warning,
  Download,
  Delete,
  RestorePage,
  Schedule
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../hooks/usePageTitle';
import axios from 'axios';

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Standalone axios instance for polling during restore (auth may be down)
const getStatusApi = () => {
  const baseURL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:8000'
    : `${window.location.protocol}//${window.location.host}`;
  return axios.create({ baseURL, withCredentials: true });
};

const Backups = () => {
  const { api } = useAuth();
  usePageTitle('Backups');

  const [dumping, setDumping] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState(null); // null | 'queued' | 'in_progress' | 'complete' | 'error'
  const [restoreMessage, setRestoreMessage] = useState('');
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Stored backups
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, filename: null });
  const [restoreStoredDialog, setRestoreStoredDialog] = useState({ open: false, filename: null });

  const fetchBackups = useCallback(async () => {
    try {
      const res = await api.get('/api/admin/backups/list/');
      setBackups(res.data.backups);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  }, [api]);

  // Poll restore status
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    const statusApi = getStatusApi();
    pollRef.current = setInterval(async () => {
      try {
        const res = await statusApi.get('/api/admin/backups/restore/status/');
        const { status: s, message, error } = res.data;
        setRestoreStatus(s);
        setRestoreMessage(message || error || '');

        if (s === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRestoring(false);
          toast.success('Backup restored successfully. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => window.location.href = '/', 2000);
        } else if (s === 'error') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRestoring(false);
          toast.error(`Restore failed: ${error}`);
        }
      } catch (err) {
        // Server might be restarting during restore — keep polling
        console.log('Status poll failed, retrying...', err.message);
      }
    }, 3000);
  }, []);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // Check if a restore is already in progress on page load
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const statusApi = getStatusApi();
        const res = await statusApi.get('/api/admin/backups/restore/status/');
        const { status: s } = res.data;
        if (s === 'queued' || s === 'in_progress') {
          setRestoring(true);
          setRestoreStatus(s);
          startPolling();
        }
      } catch (err) {
        // ignore
      }
    };
    checkStatus();
    fetchBackups();
  }, [fetchBackups, startPolling]);

  const handleDump = async () => {
    setDumping(true);
    try {
      const response = await api.get('/api/admin/backups/dump/', {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let filename = 'gopak_backup.json';
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
    } catch (err) {
      console.error('Dump failed:', err);
      toast.error('Failed to create backup');
    } finally {
      setDumping(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setRestoreDialogOpen(true);
    }
    e.target.value = '';
  };

  const queueRestore = async (formData) => {
    setRestoring(true);
    setRestoreStatus('queued');
    setRestoreMessage('Uploading and queuing restore...');
    try {
      await api.post('/api/admin/backups/restore/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min for large uploads
      });
      toast.info('Restore queued. This may take a few minutes...');
      startPolling();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to queue restore';
      toast.error(msg);
      setRestoring(false);
      setRestoreStatus(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    setRestoreDialogOpen(false);
    const formData = new FormData();
    formData.append('file', selectedFile);
    setSelectedFile(null);
    await queueRestore(formData);
  };

  const handleRestoreStored = async () => {
    const { filename } = restoreStoredDialog;
    setRestoreStoredDialog({ open: false, filename: null });
    const formData = new FormData();
    formData.append('filename', filename);
    await queueRestore(formData);
  };

  const handleDownloadStored = async (filename) => {
    try {
      const response = await api.get(`/api/admin/backups/download/${filename}/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download backup');
    }
  };

  const handleDeleteStored = async () => {
    const { filename } = deleteDialog;
    setDeleteDialog({ open: false, filename: null });
    try {
      await api.delete(`/api/admin/backups/delete/${filename}/`);
      toast.success('Backup deleted');
      fetchBackups();
    } catch (err) {
      toast.error('Failed to delete backup');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Backups
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Export the entire site database to a file, or restore from a previous backup.
        Automatic backups run every night at 2:00 AM.
      </Typography>

      <Grid container spacing={3}>
        {/* Dump / Export Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CloudDownload sx={{ fontSize: 56, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Export Backup
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Download a full snapshot of the database as a JSON file. This includes all
                products, orders, inventory, users, and configuration.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={dumping ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
                onClick={handleDump}
                disabled={dumping || restoring}
              >
                {dumping ? 'Exporting...' : 'Download Backup'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Restore Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <CloudUpload sx={{ fontSize: 56, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Restore from File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload a previously exported backup file to restore data.
                This will replace all current data with the backup contents.
              </Typography>
              <input
                type="file"
                accept=".json"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="contained"
                color="warning"
                size="large"
                startIcon={restoring ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                disabled={dumping || restoring}
              >
                {restoring ? 'Restoring...' : 'Upload & Restore'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {restoring && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {restoreStatus === 'queued' && 'Restore queued, waiting for scheduler to pick it up...'}
            {restoreStatus === 'in_progress' && 'Restoring database, this may take a few minutes...'}
            {!restoreStatus && 'Preparing restore...'}
          </Typography>
          <LinearProgress />
          {restoreMessage && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {restoreMessage}
            </Typography>
          )}
        </Paper>
      )}

      {/* Auto backup history */}
      <Paper sx={{ mt: 4 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            <Typography variant="h6">Backup History</Typography>
            <Chip label="Auto: daily at 2:00 AM" size="small" color="info" variant="outlined" />
          </Box>
          <Button size="small" onClick={fetchBackups}>Refresh</Button>
        </Box>

        {loadingBackups ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : backups.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No stored backups yet. The first automatic backup will be created tonight at 2:00 AM.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((b) => (
                  <TableRow key={b.filename} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {b.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={b.auto ? 'Auto' : 'Manual'}
                        size="small"
                        color={b.auto ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatSize(b.size)}</TableCell>
                    <TableCell>
                      {new Date(b.created).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownloadStored(b.filename)}>
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Restore from this backup">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => setRestoreStoredDialog({ open: true, filename: b.filename })}
                          disabled={restoring}
                        >
                          <RestorePage fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, filename: b.filename })}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        Automatic backups are retained for the last 30 days. Backups include all application data
        (users, products, orders, inventory, BOMs, work orders, etc.) but exclude session data and admin log entries.
        After a restore completes you will need to log in again.
      </Alert>

      {/* Upload restore confirmation dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Confirm Restore
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to restore from <strong>{selectedFile?.name}</strong>.
            This will clear all current data and replace it with the backup contents.
            You will need to log in again after the restore completes.
            This action cannot be undone. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRestoreDialogOpen(false); setSelectedFile(null); }}>
            Cancel
          </Button>
          <Button onClick={handleRestore} color="warning" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stored backup restore confirmation dialog */}
      <Dialog open={restoreStoredDialog.open} onClose={() => setRestoreStoredDialog({ open: false, filename: null })}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Confirm Restore
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to restore from <strong>{restoreStoredDialog.filename}</strong>.
            This will clear all current data and replace it with the backup contents.
            You will need to log in again after the restore completes.
            This action cannot be undone. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreStoredDialog({ open: false, filename: null })}>
            Cancel
          </Button>
          <Button onClick={handleRestoreStored} color="warning" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, filename: null })}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Delete Backup
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteDialog.filename}</strong>?
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, filename: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteStored} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Backups;
